import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUS_ACTIONS = [
  { s: "live", label: "Approve → Live" },
  { s: "needs_changes", label: "Request changes" },
  { s: "rejected", label: "Reject" },
  { s: "suspended", label: "Suspend" },
];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [pending, setPending] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [bookings, setBookings] = useState([]);

  const load = async () => {
    const [m, subs, ur, ld, st, bk] = await Promise.all([
      api.get("/admin/metrics"), api.get("/admin/listings?status_filter=submitted"),
      api.get("/admin/listings?status_filter=under_review"), api.get("/admin/leads"), api.get("/settings"),
      api.get("/admin/bookings"),
    ]);
    setMetrics(m.data); setPending([...subs.data, ...ur.data]); setLeads(ld.data); setSettings(st.data); setBookings(bk.data);
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!user.is_admin) { navigate("/"); return; }
    load().catch(() => toast.error("Failed to load admin data"));
  }, [user, loading]); // eslint-disable-line

  const setStatus = async (id, status, verified_host) => {
    try {
      await api.patch(`/listings/${id}/status`, { status, ...(verified_host !== undefined ? { verified_host } : {}) });
      toast.success(`Listing set to ${status}`);
      load();
    } catch { toast.error("Action failed"); }
  };

  const saveSettings = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
  };

  const refund = async (id) => {
    if (!window.confirm("Fully refund this booking? This refunds the renter, reverses the host payout, and can't be undone.")) return;
    try {
      const { data } = await api.post(`/admin/bookings/${id}/refund`);
      toast.success(`Refunded $${(data.refund_amount || 0).toFixed(2)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
  };

  if (!user?.is_admin) return null;
  const M = metrics || {};

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-2">ADMIN</div>
      <h1 className="font-display font-black tracking-tighter text-4xl mb-8">Control room</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { k: M.users ?? 0, v: "Users" }, { k: M.listings_submitted ?? 0, v: "Awaiting review" },
          { k: M.listings_live ?? 0, v: "Live listings" }, { k: M.bookings_completed ?? 0, v: "Bookings" },
          { k: `$${M.gross_booking_volume ?? 0}`, v: "Gross volume" }, { k: `$${M.platform_revenue ?? 0}`, v: "Platform revenue" },
          { k: M.leads ?? 0, v: "Launch leads" }, { k: M.listings_approved ?? 0, v: "Approved" },
        ].map((s) => (
          <div key={s.v} className="border border-neutral-200 rounded-xl p-5 bg-white">
            <div className="font-display font-black text-3xl">{s.k}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">{s.v}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="approvals">
        <TabsList className="mb-6">
          <TabsTrigger value="approvals" data-testid="tab-approvals">Approvals ({pending.length})</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="approvals">
          {pending.length === 0 ? (
            <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 rounded-xl p-10 text-center">No listings awaiting review.</div>
          ) : pending.map((l) => (
            <div key={l.id} className="border border-neutral-200 rounded-xl p-5 mb-4 bg-white" data-testid={`review-${l.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-display font-bold text-lg">{l.title}</div>
                  <div className="text-xs text-neutral-500">{l.org_name ? `${l.org_name} · ` : ""}{l.category} · {l.city} · {l.address} · ${l.price_per_hour}/hr · hosted by {l.host_name}</div>
                  <div className="text-sm text-neutral-600 mt-2 max-w-2xl">{l.description}</div>
                  {l.photos?.length ? <div className="flex gap-2 mt-3">{l.photos.slice(0,5).map((p,i)=><img key={i} src={p.startsWith("http")?p:`${(api.defaults.baseURL||"").replace(/\/api$/,"")}/api/files/${p}`} alt="" className="w-16 h-16 rounded object-cover border" />)}</div> : null}
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-amber-600">{l.status}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {STATUS_ACTIONS.map((a) => (
                  <Button key={a.s} size="sm" onClick={() => setStatus(l.id, a.s, a.s === "live" ? true : undefined)}
                    className={a.s === "live" ? "bg-[#FF4500] hover:bg-[#E63E00] text-white" : "bg-white border border-neutral-300 text-neutral-800 hover:border-neutral-900"}
                    data-testid={`action-${a.s}-${l.id}`}>{a.label}</Button>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="bookings">
          {bookings.length === 0 ? (
            <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 rounded-xl p-10 text-center">No bookings yet.</div>
          ) : (
            <div className="border border-neutral-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-bookings-table">
                <thead className="bg-neutral-50 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                  <tr>
                    <th className="text-left p-3">Space</th>
                    <th className="text-left p-3">Renter</th>
                    <th className="text-left p-3">When</th>
                    <th className="text-right p-3">Total</th>
                    <th className="text-left p-3">Payment</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {bookings.map((b) => {
                    const refundable = b.payment_status === "paid" || b.payment_status === "partially_refunded";
                    return (
                      <tr key={b.id} data-testid={`admin-booking-${b.id}`}>
                        <td className="p-3 font-medium">{b.listing_title}</td>
                        <td className="p-3">{b.renter_name}<div className="text-neutral-400 text-xs">{b.renter_email}</div></td>
                        <td className="p-3 whitespace-nowrap">{b.date} · {b.start_time}–{b.end_time}</td>
                        <td className="p-3 text-right font-display font-bold">${(b.total || 0).toFixed(2)}</td>
                        <td className="p-3"><span className="text-[10px] font-mono uppercase tracking-widest text-neutral-600">{b.payment_status}</span></td>
                        <td className="p-3 text-right">
                          {refundable ? (
                            <Button size="sm" onClick={() => refund(b.id)} className="bg-white border border-neutral-300 text-red-600 hover:border-red-600" data-testid={`refund-${b.id}`}>Refund</Button>
                          ) : (
                            <span className="text-xs text-neutral-400">{b.payment_status === "refunded" ? "Refunded" : "—"}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="leads">
          {leads.length === 0 ? <div className="text-sm text-neutral-500">No launch leads yet.</div> :
            <div className="border border-neutral-200 rounded-xl divide-y">
              {leads.map((l) => <div key={l.email} className="p-3 flex justify-between text-sm"><span>{l.email}</span><span className="text-neutral-400">{l.city}</span></div>)}
            </div>}
        </TabsContent>

        <TabsContent value="settings">
          {settings && (
            <div className="max-w-md space-y-4">
              <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={settings.founding_promo_active} onChange={(e) => setSettings({ ...settings, founding_promo_active: e.target.checked })} data-testid="set-promo-active" /> Founding-Host promo active</label>
              <div><div className="text-xs text-neutral-500 mb-1">Founding fee %</div><Input type="number" value={settings.founding_fee_pct} onChange={(e) => setSettings({ ...settings, founding_fee_pct: Number(e.target.value) })} data-testid="set-founding-fee" /></div>
              <div><div className="text-xs text-neutral-500 mb-1">Founding months</div><Input type="number" value={settings.founding_months} onChange={(e) => setSettings({ ...settings, founding_months: Number(e.target.value) })} data-testid="set-founding-months" /></div>
              <div><div className="text-xs text-neutral-500 mb-1">Standard fee %</div><Input type="number" value={settings.standard_fee_pct} onChange={(e) => setSettings({ ...settings, standard_fee_pct: Number(e.target.value) })} data-testid="set-standard-fee" /></div>
              <div><div className="text-xs text-neutral-500 mb-1">Launch city</div><Input value={settings.launch_city} onChange={(e) => setSettings({ ...settings, launch_city: e.target.value })} data-testid="set-launch-city" /></div>
              <Button onClick={saveSettings} className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full" data-testid="save-settings">Save settings</Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
